import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, Heart, Image as ImageIcon, Trophy, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getShareGalleryRanking } from "../../services/galleryService";
import Modal from "../common/Modal";
import Loader from "../common/Loader";
import Badge from "../common/Badge";
import { color, radius } from "../../styles/theme";

const getGalleryStatus = (gallery, event) => {
  if (!gallery?.ShareBannerActive) return "deactivated";

  const now = Date.now();
  const startsAt = event?.StartDateTime ? new Date(event.StartDateTime).getTime() : null;
  const endsAt = event?.PromtorEventEndDateTime
    ? new Date(event.PromtorEventEndDateTime).getTime()
    : null;

  if (startsAt && now < startsAt) return "prepared";
  if (startsAt && now >= startsAt && (!endsAt || now <= endsAt)) return "running";
  return "active";
};

const getInitials = (name) =>
  (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const ShareGalleryConsoleModal = ({ event, gallery, onClose }) => {
  const { t, i18n } = useTranslation("eventDetails");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    getShareGalleryRanking(event.Id)
      .then((res) => {
        if (active) setData(res.data?.Result || null);
      })
      .catch((err) => {
        if (active) setError(err.response?.data?.Message || t("shareGalleryConsole.loadError"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [event.Id, t]);

  const preview = data?.PromoterSettlementPreview || data?.AdminSettlementPreview || null;
  const financialsByUser = useMemo(
    () => new Map((preview?.Uploaders || []).map((item) => [String(item.UserId), item])),
    [preview]
  );
  const currency = data?.Currency || gallery?.Currency || gallery?.Package?.Currency || "";
  const formatMoney = (amount) => {
    if (amount == null) return "—";
    try {
      return new Intl.NumberFormat(i18n.language, {
        style: currency ? "currency" : "decimal",
        currency: currency || undefined,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(amount));
    } catch {
      return `${Number(amount).toFixed(2)} ${currency}`.trim();
    }
  };

  const status = getGalleryStatus(gallery, event);
  const price = gallery?.Price ?? gallery?.Package?.DefaultPrice;
  const summary = data?.EventSummary || {};

  return (
    <Modal
      title={t("shareGalleryConsole.title")}
      subtitle={gallery?.Name}
      onClose={onClose}
      maxWidth={980}
    >
      {loading ? <Loader /> : error ? (
        <div style={s.errorBox}><AlertTriangle size={18} /> <span>{error}</span></div>
      ) : (
        <div style={s.content}>
          <div style={s.statusRow}>
            <div>
              <span style={s.eyebrow}>{t("shareGalleryConsole.galleryStatus")}</span>
              <div style={s.statusValue}>
                <span style={{ ...s.statusDot, background: status === "deactivated" ? color.gray400 : color.success500 }} />
                {t(`shareGalleryConsole.status.${status}`)}
              </div>
            </div>
            <div style={s.settlementState}>
              <span style={s.eyebrow}>{t("shareGalleryConsole.settlementStatus")}</span>
              <Badge variant={data?.SettlementStatus === "Locked" ? "success" : "warning"}>
                {data?.SettlementStatus === "Locked"
                  ? t("shareGalleryConsole.settlement.locked")
                  : t("shareGalleryConsole.settlement.preview")}
              </Badge>
            </div>
          </div>

          <div style={s.metricGrid}>
            <Metric icon={Users} label={t("shareGalleryConsole.metrics.paidAccesses")} value={summary.Users ?? data?.PaidAccessCount ?? 0} />
            <Metric icon={ImageIcon} label={t("shareGalleryConsole.metrics.scoredMedia")} value={summary.MediaFiles ?? data?.TotalMediaUploaded ?? 0} />
            <Metric icon={Trophy} label={t("shareGalleryConsole.metrics.shareAmount")} value={formatMoney(summary.ShareAmount)} highlight />
            <Metric label={t("shareGalleryConsole.metrics.accessPrice")} value={price == null ? t("shareGalleryConsole.notConfigured") : formatMoney(price)} />
          </div>

          {!preview && (
            <div style={s.noticeBox}>
              <AlertTriangle size={17} />
              <span>{t("shareGalleryConsole.financialDetailsUnavailable")}</span>
            </div>
          )}

          {preview && (
            <div style={s.allocationStrip}>
              <AllocationItem label={t("shareGalleryConsole.verifiedVolume")} value={formatMoney(preview.VerifiedAccessReceiptAmount)} />
              <AllocationItem label={t("shareGalleryConsole.shareRate")} value={`${Math.round(Number(preview.ShareRate || 0) * 100)}%`} />
              <AllocationItem label={t("shareGalleryConsole.minimumDistribution")} value={formatMoney(preview.MinimumDistributionAmount)} />
            </div>
          )}

          <div style={s.sectionHeader}>
            <div>
              <h4 style={s.sectionTitle}>{t("shareGalleryConsole.rankingTitle")}</h4>
              <p style={s.sectionSubtitle}>{t("shareGalleryConsole.rankingSubtitle")}</p>
            </div>
            <span style={s.uploaderCount}>{t("shareGalleryConsole.uploaderCount", { count: data?.Items?.length || 0 })}</span>
          </div>

          {!data?.Items?.length ? (
            <div style={s.empty}>{t("shareGalleryConsole.emptyRanking")}</div>
          ) : (
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>{t("shareGalleryConsole.columns.rank")}</th>
                    <th style={s.th}>{t("shareGalleryConsole.columns.uploader")}</th>
                    <th style={s.th}>{t("shareGalleryConsole.columns.media")}</th>
                    <th style={s.th}><Heart size={12} /> {t("shareGalleryConsole.columns.likes")}</th>
                    <th style={s.th}><Download size={12} /> {t("shareGalleryConsole.columns.downloads")}</th>
                    <th style={s.th}>{t("shareGalleryConsole.columns.points")}</th>
                    <th style={s.th}>{t("shareGalleryConsole.columns.currentShare")}</th>
                    <th style={s.th}>{t("shareGalleryConsole.columns.mda")}</th>
                    <th style={s.th}>{t("shareGalleryConsole.columns.finalShare")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.Items.map((item) => {
                    const financial = financialsByUser.get(String(item.UserId));
                    const finalAmount = data.SettlementStatus === "Locked"
                      ? financial?.FinalDistributionAmount
                      : financial?.MdaReached
                        ? Number(financial?.CurrentShareAmount || 0) + Number(financial?.RedistributionAmount || 0)
                        : 0;
                    return (
                      <tr key={item.UserId}>
                        <td style={{ ...s.td, ...s.rankCell }}>#{item.Position}</td>
                        <td style={s.td}>
                          <div style={s.userCell}>
                            {item.DisplayPicture
                              ? <img src={item.DisplayPicture} alt="" style={s.avatar} />
                              : <span style={s.avatarFallback}>{getInitials(item.NickName)}</span>}
                            <span style={s.userName}>{item.NickName || t("shareGalleryConsole.unnamedUploader")}</span>
                          </div>
                        </td>
                        <td style={s.td}>{item.ApprovedMediaCount}</td>
                        <td style={s.td}>{item.LikeCount}</td>
                        <td style={s.td}>{item.DownloadCount}</td>
                        <td style={{ ...s.td, fontWeight: 700 }}>{item.RankingScore}</td>
                        <td style={s.td}>{financial ? formatMoney(financial.CurrentShareAmount) : "—"}</td>
                        <td style={s.td}>
                          {financial ? (
                            <span style={{ ...s.mdaPill, ...(financial.MdaReached ? s.mdaReached : s.mdaPending) }}>
                              {financial.MdaReached && <CheckCircle2 size={12} />}
                              {financial.MdaReached
                                ? t("shareGalleryConsole.mda.reached")
                                : t("shareGalleryConsole.mda.notReached")}
                            </span>
                          ) : "—"}
                        </td>
                        <td style={{ ...s.td, fontWeight: 700 }}>{financial ? formatMoney(finalAmount) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p style={s.privacyNote}>{t("shareGalleryConsole.privacyNote")}</p>
        </div>
      )}
    </Modal>
  );
};

const Metric = ({ icon: Icon, label, value, highlight = false }) => (
  <div style={{ ...s.metric, ...(highlight ? s.metricHighlight : {}) }}>
    <div style={s.metricLabel}>{Icon && <Icon size={14} />} {label}</div>
    <strong style={s.metricValue}>{value}</strong>
  </div>
);

const AllocationItem = ({ label, value }) => (
  <div style={s.allocationItem}>
    <span style={s.allocationLabel}>{label}</span>
    <strong style={s.allocationValue}>{value}</strong>
  </div>
);

const s = {
  content: { display: "flex", flexDirection: "column", gap: 18 },
  errorBox: { display: "flex", gap: 9, alignItems: "center", color: color.danger600, background: color.danger50, borderRadius: radius.md, padding: 14 },
  statusRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 18, flexWrap: "wrap", paddingBottom: 14, borderBottom: `1px solid ${color.border}` },
  eyebrow: { display: "block", color: color.textMuted, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 5 },
  statusValue: { display: "flex", alignItems: "center", gap: 7, color: color.textPrimary, fontSize: 15, fontWeight: 700 },
  statusDot: { width: 9, height: 9, borderRadius: "50%" },
  settlementState: { textAlign: "right" },
  metricGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 10 },
  metric: { border: `1px solid ${color.border}`, borderRadius: radius.md, padding: "13px 14px", background: color.surface },
  metricHighlight: { background: color.primary50, borderColor: color.primary100 },
  metricLabel: { display: "flex", alignItems: "center", gap: 6, color: color.textSecondary, fontSize: 11.5, fontWeight: 600, marginBottom: 7 },
  metricValue: { color: color.textPrimary, fontSize: 18 },
  noticeBox: { display: "flex", gap: 8, alignItems: "center", padding: 12, borderRadius: radius.md, color: color.warning500, background: color.warning50, fontSize: 12.5 },
  allocationStrip: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 1, overflow: "hidden", border: `1px solid ${color.border}`, borderRadius: radius.md, background: color.border },
  allocationItem: { display: "flex", flexDirection: "column", gap: 4, padding: "11px 13px", background: color.surface },
  allocationLabel: { color: color.textSecondary, fontSize: 11.5 },
  allocationValue: { color: color.textPrimary, fontSize: 14 },
  sectionHeader: { display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, marginTop: 2 },
  sectionTitle: { margin: 0, color: color.textPrimary, fontSize: 15.5 },
  sectionSubtitle: { margin: "3px 0 0", color: color.textSecondary, fontSize: 12 },
  uploaderCount: { color: color.textSecondary, background: color.gray100, borderRadius: radius.pill, padding: "4px 9px", fontSize: 11.5, whiteSpace: "nowrap" },
  tableWrap: { overflowX: "auto", border: `1px solid ${color.border}`, borderRadius: radius.md },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 850, fontSize: 12.5 },
  th: { padding: "10px 11px", textAlign: "left", background: color.gray50, color: color.textSecondary, borderBottom: `1px solid ${color.border}`, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap" },
  td: { padding: "11px", color: color.textPrimary, borderBottom: `1px solid ${color.border}`, whiteSpace: "nowrap" },
  rankCell: { fontWeight: 800, color: color.primary600 },
  userCell: { display: "flex", alignItems: "center", gap: 8 },
  avatar: { width: 28, height: 28, borderRadius: "50%", objectFit: "cover", background: color.gray100 },
  avatarFallback: { width: 28, height: 28, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", background: color.primary50, color: color.primary700, fontSize: 10, fontWeight: 800 },
  userName: { maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" },
  mdaPill: { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 7px", borderRadius: radius.pill, fontSize: 10.5, fontWeight: 700 },
  mdaReached: { color: color.success700, background: color.success50 },
  mdaPending: { color: color.gray600, background: color.gray100 },
  empty: { textAlign: "center", padding: 26, color: color.textMuted, background: color.gray50, borderRadius: radius.md },
  privacyNote: { margin: 0, color: color.textMuted, fontSize: 11.5 },
};

export default ShareGalleryConsoleModal;
